import trpcApi, { ApiInputs } from 'lib/trpcClient';
import { FrontEndSiteSubdomainUrlToStageMap } from 'adminConstants/redirectConstant';
import { useNotification } from 'hooks/ui/useNotification';

type BaseParams = {
  firmDomain: string;
};
type UseLaunchPortalImpersonateInvestorMutateParams =
  ApiInputs['impersonationAccess']['impersonateInvestor'] & BaseParams;
type UseLaunchPortalImpersonateAdviserMutateParams =
  ApiInputs['impersonationAccess']['impersonateAdviser'] & BaseParams;
type UseLaunchPortalImpersonateOrgUserMutateParams =
  ApiInputs['impersonationAccess']['impersonateOrgUser'] & BaseParams;

export function useLaunchPortalImpersonation(redirectTo?: string) {
  const stage = process.env.REACT_APP_STAGE ?? '';
  const { error } = useNotification();

  const onSuccess = (firmDomain: string, windowRef: Window | null) => {
    const host = FrontEndSiteSubdomainUrlToStageMap[stage].replace(
      '{domain}',
      firmDomain,
    );
    const url = `${host}${redirectTo ?? ''}`;

    if (windowRef && !windowRef.closed) {
      windowRef.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  };

  const onError = (
    err:
      | { data?: { cause?: string | null } | null; message?: string }
      | undefined
      | null,
    windowRef: Window | null,
  ) => {
    if (windowRef && !windowRef.closed) {
      windowRef.close();
    }
    error(err?.data?.cause ?? err?.message ?? 'Something went wrong.');
  };

  const hostname = window.location.hostname;
  const isClassicLocalhost =
    hostname === 'localhost' || hostname.endsWith('.localhost');

  type ImpersonationResponse = {
    token: string;
  };

  const openLocalEntryWithToken = (
    firmDomain: string,
    token: string,
    windowRef: Window | null,
  ) => {
    const host = FrontEndSiteSubdomainUrlToStageMap[stage].replace(
      '{domain}',
      firmDomain,
    );
    const entryUrl = `${host}/admin-impersonation-entry#token=${encodeURIComponent(
      token,
    )}&redirectTo=${encodeURIComponent(redirectTo ?? '')}`;

    if (windowRef && !windowRef.closed) {
      windowRef.location.href = entryUrl;
    } else {
      window.open(entryUrl, '_blank');
    }
  };

  const handleLocalImpersonationSuccess = (
    data: ImpersonationResponse,
    firmDomain: string,
    windowRef: Window | null,
  ) => {
    const token = data.token;
    if (!token) {
      onError({ message: 'Impersonation token missing.' }, windowRef);
      return;
    }
    openLocalEntryWithToken(firmDomain, token, windowRef);
  };

  const { mutate: trpcMutateInvestor, isPending: isInvestorPending } =
    trpcApi.impersonationAccess.impersonateInvestor.useMutation();
  const { mutate: trpcMutateAdviser, isPending: isAdviserPending } =
    trpcApi.impersonationAccess.impersonateAdviser.useMutation();
  const { mutate: trpcMutateOrgUser, isPending: isOrgUserPending } =
    trpcApi.impersonationAccess.impersonateOrgUser.useMutation();

  const investor = (params: UseLaunchPortalImpersonateInvestorMutateParams) => {
    const { firmDomain, ...apiParams } = params;

    const newWindow = window.open('about:blank', '_blank');

    if (isClassicLocalhost) {
      trpcMutateInvestor(apiParams, {
        onSuccess: (data: ImpersonationResponse) =>
          handleLocalImpersonationSuccess(data, firmDomain, newWindow),
        onError: (err) => onError(err, newWindow),
      });
      return;
    }

    trpcMutateInvestor(apiParams, {
      onSuccess: () => onSuccess(firmDomain, newWindow),
      onError: (err) => onError(err, newWindow),
    });
  };

  const adviser = (params: UseLaunchPortalImpersonateAdviserMutateParams) => {
    const { firmDomain, ...apiParams } = params;

    const newWindow = window.open('about:blank', '_blank');

    if (isClassicLocalhost) {
      trpcMutateAdviser(apiParams, {
        onSuccess: (data: ImpersonationResponse) =>
          handleLocalImpersonationSuccess(data, firmDomain, newWindow),
        onError: (err) => onError(err, newWindow),
      });
      return;
    }

    trpcMutateAdviser(apiParams, {
      onSuccess: () => onSuccess(firmDomain, newWindow),
      onError: (err) => onError(err, newWindow),
    });
  };

  const orgUser = (params: UseLaunchPortalImpersonateOrgUserMutateParams) => {
    const { firmDomain, ...apiParams } = params;

    const newWindow = window.open('about:blank', '_blank');

    if (isClassicLocalhost) {
      trpcMutateOrgUser(apiParams, {
        onSuccess: (data: ImpersonationResponse) =>
          handleLocalImpersonationSuccess(data, firmDomain, newWindow),
        onError: (err) => onError(err, newWindow),
      });
      return;
    }

    trpcMutateOrgUser(apiParams, {
      onSuccess: () => onSuccess(firmDomain, newWindow),
      onError: (err) => onError(err, newWindow),
    });
  };

  return {
    investor,
    adviser,
    orgUser,
    isPending: isInvestorPending || isAdviserPending || isOrgUserPending,
  };
}
